import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';
import StatusChip from './StatusChip';

const fmtMMSS = (s: number | null | undefined) => {
  if (s == null) return '—';
  const sec = Math.max(0, Math.floor(s));
  const m = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
};

const fmtDurSec = (s: number | null | undefined) => {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}m${rem ? `${rem}s` : ''}`;
};

const fmtSinceSeconds = (s: number | null | undefined) => {
  if (s == null) return '—';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
};

const WorkersTab: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['admin-running'],
    queryFn: adminAPI.getRunning,
    refetchInterval: 3000,
    staleTime: 2000,
  });

  const running = data?.running || [];
  const overdue = data?.overdue || [];
  const recent = data?.recent || [];

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Panel label="RUNNING NOW">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>JOB</TableCell>
                <TableCell>STARTED</TableCell>
                <TableCell align="right">RUNNING FOR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {running.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="caption" color="textSecondary">
                      No jobs currently running.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                running.map((r: any) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.job_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.started_at ? new Date(r.started_at).toLocaleTimeString() : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "'VT323', monospace" }}>
                      {fmtMMSS(r.running_for_seconds)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Panel>

      <Panel label="OVERDUE">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>JOB</TableCell>
                <TableCell>CRON</TableCell>
                <TableCell>LAST RUN</TableCell>
                <TableCell align="right">SINCE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overdue.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="caption" color="textSecondary">
                      No overdue jobs — everything on schedule.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                overdue.map((o: any) => (
                  <TableRow key={o.job_name} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{o.job_name}</Typography>
                    </TableCell>
                    <TableCell><code>{o.cron_string}</code></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {o.last_run_at ? new Date(o.last_run_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="error" fontWeight={600} variant="body2">
                        {fmtSinceSeconds(o.seconds_since_last_run)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Panel>

      <Panel label="RECENT (15m)">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>STARTED</TableCell>
                <TableCell>JOB</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell align="right">DURATION</TableCell>
                <TableCell align="right">RECORDS</TableCell>
                <TableCell>COMPLETED</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recent.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="caption" color="textSecondary">
                      No completed runs in the last 15 minutes.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recent.map((r: any) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.started_at ? new Date(r.started_at).toLocaleTimeString() : '—'}
                    </TableCell>
                    <TableCell>{r.job_name}</TableCell>
                    <TableCell><StatusChip status={r.status} /></TableCell>
                    <TableCell align="right">{fmtDurSec(r.duration_seconds)}</TableCell>
                    <TableCell align="right">{(r.records_processed ?? 0).toLocaleString()}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.completed_at ? new Date(r.completed_at).toLocaleTimeString() : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Panel>
    </Box>
  );
};

export default WorkersTab;
